import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

import { FileText, Plus, Edit, Trash2, Copy, Eye, EyeOff, Search, Tag, FolderOpen, FileDown, Printer, Share2 } from 'lucide-react';

import BackButton from '../../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const _API = `${BACKEND_URL}/api`;

import { useTemplatesExperts } from './useTemplatesExperts';
import TemplatesModals from './TemplatesModals';

export default function TemplatesExpertsManagement({ getAuthHeader }) {
  const ctx = useTemplatesExperts(getAuthHeader);
  const { categories, filterCategory, filterType, filteredTemplates, handleCreateNew, handleDelete, handleDownloadPDF, handleDuplicate, handleEdit, handlePreviewTemplate, handlePrintTemplate, handleShareTemplate, handleToggleActive, loading, searchQuery, setFilterCategory, setFilterType, setSearchQuery, templates: templates, types } = ctx;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        
      {/* Bouton Retour */}
      <BackButton label="Retour" />

<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Templates Experts</h2>
          <p className="text-slate-600 mt-1">Gérez les modèles de documents pour les experts</p>
        </div>
        <Button onClick={handleCreateNew} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" />
          Nouveau Template
        </Button>
      </div>

      {/* Filtres et recherche */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher un template..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtre catégorie */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Toutes catégories</option>
              {categories.all.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Filtre type */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous types</option>
              {types.all.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des templates */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTemplates.length === 0 ? (
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <FileText className="mx-auto h-16 w-16 text-slate-300 mb-4" />
                <p className="text-slate-600 text-lg">Aucun template trouvé</p>
                <p className="text-slate-400 text-sm mt-2">
                  {searchQuery ? 'Essayez d\'ajuster vos critères de recherche' : 'Commencez par créer un nouveau template'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredTemplates.map(template => (
            <Card key={template.id} className={`${!template.is_active ? 'opacity-60' : ''} hover:shadow-lg transition-shadow`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      {template.name}
                    </CardTitle>
                    <CardDescription className="mt-2">
                      {template.description || 'Aucune description'}
                    </CardDescription>
                  </div>
                  <Badge variant={template.is_active ? 'default' : 'secondary'}>
                    {template.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Catégorie et Type */}
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-purple-600 text-white shadow-md font-semibold">
                      <FolderOpen className="mr-1 h-3 w-3" />
                      {template.category}
                    </Badge>
                    <Badge className="bg-blue-600 text-white shadow-md font-semibold">
                      {template.type}
                    </Badge>
                  </div>

                  {/* Tags */}
                  {template.tags && template.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {template.tags.map(tag => (
                        <Badge key={tag} className="bg-blue-600 text-white text-xs font-semibold">
                          <Tag className="mr-1 h-3 w-3" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Métadonnées */}
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>Créé le: {new Date(template.created_at).toLocaleDateString('fr-FR')}</p>
                    {template.updated_at && (
                      <p>Modifié le: {new Date(template.updated_at).toLocaleDateString('fr-FR')}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-4 border-t">
                    {/* Actions principales */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handlePreviewTemplate(template)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        title="Voir le template"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Voir
                      </Button>
                      <Button
                        onClick={() => handleEdit(template)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Éditer
                      </Button>
                      <Button
                        onClick={() => handleDuplicate(template)}
                        size="sm"
                        variant="outline"
                        title="Dupliquer ce template"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Actions de document */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handlePrintTemplate(template)}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Imprimer"
                      >
                        <Printer className="mr-2 h-4 w-4" />
                        Imprimer
                      </Button>
                      <Button
                        onClick={() => handleDownloadPDF(template)}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        title="Télécharger PDF"
                      >
                        <FileDown className="mr-2 h-4 w-4" />
                        PDF
                      </Button>
                      <Button
                        onClick={() => handleShareTemplate(template)}
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        title="Partager"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Actions de gestion */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleToggleActive(template)}
                        size="sm"
                        variant={template.is_active ? "outline" : "default"}
                        className={`flex-1 ${template.is_active ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        {template.is_active ? (
                          <><EyeOff className="mr-2 h-4 w-4" />Désactiver</>
                        ) : (
                          <><Eye className="mr-2 h-4 w-4" />Activer</>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDelete(template.id)}
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <TemplatesModals ctx={ctx} />
    </div>
  );
}
